import { Check } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  rbacService,
  PermissionRecord,
  RoleRecord,
} from "@/services/rbac.service";

const DEFAULT_ROLE_ORDER = [
  "BUSINESS_OWNER",
  "STORE_MANAGER",
  "PHARMACIST",
  "SUPER_ADMIN",
];

export function PermissionsPage() {
  const [permissions, setPermissions] = useState<PermissionRecord[]>([]);
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        const [permissionsResponse, rolesResponse] = await Promise.all([
          rbacService.getPermissions(),
          rbacService.getRoles(),
        ]);

        if (!mounted) return;

        setPermissions(permissionsResponse || []);
        setRoles(rolesResponse || []);
        setError(null);
      } catch (err: any) {
        if (!mounted) return;
        setError(err?.response?.data?.message || "Failed to load RBAC data");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, []);

  const permissionGroups = useMemo(() => {
    const groups = new Map<string, string[]>();

    permissions.forEach((permission) => {
      const keys = groups.get(permission.group) || [];
      keys.push(permission.key);
      groups.set(permission.group, keys);
    });

    return Array.from(groups.entries()).map(([title, keys]) => ({
      title,
      keys,
    }));
  }, [permissions]);

  const roleOrder = useMemo(() => {
    const roleNames = roles.map((role) => role.name);
    const ordered = DEFAULT_ROLE_ORDER.filter((role) =>
      roleNames.includes(role),
    );
    const remaining = roleNames.filter((name) => !ordered.includes(name));
    return [...ordered, ...remaining];
  }, [roles]);

  const permissionsByRole = useMemo(() => {
    const map = new Map<string, string[]>();
    roles.forEach((role) => map.set(role.name, role.permissions || []));
    return map;
  }, [roles]);

  return (
    <div className="min-h-screen bg-slate-50/50 p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
          Permissions Matrix
        </h1>
        <p className="text-slate-500 mt-2 text-lg font-light">
          Role-based access control overview
        </p>
      </div>

      {loading && (
        <div className="rounded-2xl border border-slate-200 bg-white/80 p-6 text-slate-500 shadow-sm">
          Loading RBAC data...
        </div>
      )}

      {!loading && error && (
        <div className="rounded-2xl border border-red-200 bg-red-50/80 p-6 text-red-600 shadow-sm">
          {error}
        </div>
      )}

      {!loading && !error && (
        <div className="space-y-8 pb-12">
          {permissionGroups.map((group) => (
            <div
              key={group.title}
              className="relative overflow-hidden rounded-2xl border border-slate-200/60 bg-white/70 backdrop-blur-xl shadow-xl shadow-slate-200/40 transition-all hover:bg-white/90"
            >
              {/* Subtle decorative gradient blob */}
              <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-indigo-500/5 blur-3xl pointer-events-none" />

              <div className="px-8 py-6 border-b border-slate-100 relative z-10 bg-white/50">
                <h2 className="text-xl font-semibold text-slate-800 tracking-wide flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-indigo-500 shadow-[0_0_0_2px_rgba(99,102,241,0.2)]" />
                  {group.title}
                </h2>
              </div>

              <div className="overflow-x-auto relative z-10">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/50">
                      <th className="px-8 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                        Permission
                      </th>
                      {roleOrder.map((role) => (
                        <th
                          key={role}
                          className="px-6 py-4 text-center text-xs font-bold text-slate-400 uppercase tracking-wider"
                        >
                          {role.replace(/_/g, " ")}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100/80">
                    {group.keys.map((permission) => (
                      <tr
                        key={permission}
                        className="group/row transition-colors hover:bg-slate-50/80"
                      >
                        <td className="px-8 py-4 text-sm font-medium text-slate-600 group-hover/row:text-slate-900 transition-colors">
                          {permission}
                        </td>
                        {roleOrder.map((role) => {
                          const allowed =
                            permissionsByRole.get(role)?.includes(permission) ||
                            false;
                          return (
                            <td key={role} className="px-6 py-4 text-center">
                              {allowed ? (
                                <div className="flex justify-center">
                                  <div className="rounded-full bg-emerald-100/80 p-1.5 shadow-sm shadow-emerald-100">
                                    <Check
                                      className="w-4 h-4 text-emerald-600"
                                      strokeWidth={3}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <span className="text-slate-300 font-light select-none text-xl">
                                  ·
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
